import React, { useState } from 'react';
import FormField from '../../components/common/FormField';
import Form from '../../components/common/Form';
import FormSection from '../../components/common/FormSection';
import { VatRateHistoryDTO } from '../../services/apiClient';
import handleChange from '../../utils/formUtils/handleChange';
import { ValidationRules, useFormValidation } from '../../hooks/useFormValidation';

interface VatRateHistoryFormProps {
  initialValues: VatRateHistoryDTO;
  onSubmit: (data: VatRateHistoryDTO) => void;
  onCancel: () => void;
}

const FORM_SECTIONS = {
  basicInfo: {
    id: 'basic-info',
    title: 'VAT Rate',
  },
} as const;

const VatRateHistoryForm: React.FC<VatRateHistoryFormProps> = ({
  initialValues,
  onSubmit,
  onCancel,
}) => {
  const [formData, setFormData] = useState<VatRateHistoryDTO>(initialValues);

  const validationRules: ValidationRules = {
    rate: [
      { required: true, message: 'Rate is required' },
      {
        custom: (value) => Number(value) >= 0 && Number(value) <= 100,
        message: 'Rate must be a percentage between 0 and 100',
      },
    ],
    effectiveDate: [{ required: true, message: 'Effective Date is required' }],
  };

  const { errors, validate } =
    useFormValidation<VatRateHistoryDTO>(validationRules);

  const handleSave = () => {
    if (validate(formData)) {
      onSubmit(formData);
    }
  };

  const sections = Object.values(FORM_SECTIONS);

  return (
    <Form sections={sections} onSave={handleSave} onCancel={onCancel}>
      <FormSection
        id={FORM_SECTIONS.basicInfo.id}
        title={FORM_SECTIONS.basicInfo.title}
      >
        <FormField
          id="rate"
          label="Rate (%)"
          type="number"
          value={formData.rate ?? ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter VAT Rate, e.g. 15"
          required={true}
          error={errors.rate}
          helper="Percentage, e.g. enter 15 for 15%."
        />
        <FormField
          id="effectiveDate"
          label="Effective Date"
          type="date"
          value={formData.effectiveDate || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter Effective Date"
          required={true}
          error={errors.effectiveDate}
          helper="The rate applies to any period on or after this date, until the next effective date."
        />
      </FormSection>
    </Form>
  );
};

export default VatRateHistoryForm;
